import { useState } from "react";
import { Plus } from "lucide-react";
import { v4 } from "uuid";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextInput } from "@/shared/components/form/text-input";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import {
  CreateMasterDataPdfRequest,
  useCreateMasterDataPdfMutation,
} from "@/services/backend/company/api/master-data-pdf";

export function MasterDataPdfCreateDialog({
  companyId,
  onCreated = undefined,
}: {
  companyId: string;
  onCreated?: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(newRequest(companyId));
  const [create, { isLoading, error, reset }] =
    useCreateMasterDataPdfMutation();

  const doCreate = () => {
    if (isLoading) {
      return;
    }
    const { id } = request;
    create(request)
      .unwrap()
      .then(() => {
        toast.success(t("PDF-Template-Daten erstellt"));
        setRequest(newRequest(companyId));
        reset();
        setOpen(false);
        onCreated?.(id);
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger
        icon={Plus}
        label={t("PDF-Template-Daten")}
        size="sm"
      />
      <DialogContent>
        <CommonDialogHeader
          title={t("PDF-Template-Daten erstellen")}
          description={t(
            "Die PDF-Template-Daten liefern Briefkopf, Adressfeld, Infoblock und Fußzeile der Dokumente des Unternehmens. Die Bezeichnung unterscheidet sie von den anderen PDF-Template-Daten des Unternehmens.",
          )}
        />
        <div className="grid gap-4">
          <TextInput
            label={t("Bezeichnung")}
            field="label"
            text={request.label}
            onTextChange={(label) => setRequest({ ...request, label })}
            errors={inputValidationErrorMessages(error, "label")}
          />
        </div>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          actionLabel={t("Erstellen")}
          onClickAction={doCreate}
        />
      </DialogContent>
    </Dialog>
  );
}

function newRequest(companyId: string): CreateMasterDataPdfRequest {
  return {
    id: v4(),
    companyId,
    label: "",
  };
}
